import type { ChecklistItem as BoardChecklistItem } from '../../types/schema'
import type { SliceableWire } from '../DrawingBoard/VectorGestureKnife'
import type { DragState } from './canvasMisc'
import { isGateNode } from './boardItem'
import {
  sampleCubicConnectorPath,
  toKnifePoint,
  type AnchorName,
  type WireEndpointSlot,
} from './wireGeometry'

export type ConnectorPath = {
  id: string
  sourceId: string
  targetId: string
  from: { x: number; y: number }
  to: { x: number; y: number }
  path: string
  strokeWidth: number
  opacity: number
  className: string
  stroke?: string
  stubKind: 'none' | 'outgoing' | 'incoming'
}

export interface UseBoardWireGeometryParams {
  displayNodes: BoardChecklistItem[]
  boardScopedNodes: BoardChecklistItem[]
  getConnectorRendering: (source: BoardChecklistItem) => {
    strokeWidth: number
    opacity: number
    className: string
    stroke?: string
  }
  nodesById: Map<string, BoardChecklistItem>
  canEditCanvas: boolean
  animateBreakingWire: (source: BoardChecklistItem, target: BoardChecklistItem) => void
  commitLocalNodes: (updater: (current: BoardChecklistItem[]) => BoardChecklistItem[]) => void
  showFlash: (message: string) => void
  wireDraft: Extract<DragState, { type: 'wire' }> | null
  boardMetrics: { offsetX: number; offsetY: number }
  optimisticPositions: Record<string, { x: number; y: number }>
  getRenderedNode: (node: BoardChecklistItem) => BoardChecklistItem
  getRenderedAnchorPoint: (node: BoardChecklistItem, anchor: AnchorName) => { x: number; y: number }
  getRenderedWireEndpoint: (node: BoardChecklistItem, direction: 'in' | 'out', slot?: WireEndpointSlot) => { x: number; y: number }
  boardScale: number
  viewportSize: { scrollLeft: number; scrollTop: number }
  nodes: BoardChecklistItem[]
}

export function useBoardWireGeometry({
  displayNodes,
  boardScopedNodes,
  getConnectorRendering,
  nodesById,
  canEditCanvas,
  animateBreakingWire,
  commitLocalNodes,
  showFlash,
  wireDraft,
  boardMetrics,
  optimisticPositions,
  getRenderedNode,
  getRenderedAnchorPoint,
  getRenderedWireEndpoint,
  boardScale,
  viewportSize,
  nodes,
}: UseBoardWireGeometryParams) {
  const visibleNodeIds = new Set(displayNodes.map((node) => node.id))
  // Length of the fade-out tail drawn when a wire's other endpoint is
  // filtered out of the current breadcrumb view. Long enough to read as a
  // wire trailing off, short enough not to visually collide with adjacent
  // nodes on the same canvas.
  const STUB_WIRE_LENGTH = 80
  type ConnectorPath = {
    id: string
    sourceId: string
    targetId: string
    from: { x: number; y: number }
    to: { x: number; y: number }
    path: string
    strokeWidth: number
    opacity: number
    className: string
    stroke?: string
    stubKind: 'none' | 'outgoing' | 'incoming'
  }
  // Emit a wire when AT LEAST ONE endpoint is on-screen. If both endpoints
  // are visible → normal wire. If only the source is visible → stub tail
  // going downwards into empty space (target is off-canvas). If only the
  // target is visible → stub tail coming down from off-canvas into the
  // target. Both invisible → skip. This restores cross-parent
  // dependencies as first-class visual affordances under the breadcrumb
  // filter — hidden endpoints no longer erase the wire.
  const buildConnectorEntry = (
    idPrefix: string,
    source: BoardChecklistItem,
    target: BoardChecklistItem,
    outSlot?: WireEndpointSlot,
    inSlot?: WireEndpointSlot,
  ): ConnectorPath | null => {
    const sourceVisible = visibleNodeIds.has(source.id)
    const targetVisible = visibleNodeIds.has(target.id)
    if (!sourceVisible && !targetVisible) return null
    const rendering = getConnectorRendering(source)
    if (sourceVisible && targetVisible) {
      const from = getRenderedWireEndpoint(source, 'out', outSlot)
      const to = getRenderedWireEndpoint(target, 'in', inSlot)
      const midY = Math.max(from.y + 28, (from.y + to.y) / 2)
      return {
        id: `${idPrefix}${source.id}-${target.id}`,
        sourceId: source.id,
        targetId: target.id,
        from,
        to,
        path: `M ${from.x} ${from.y} C ${from.x} ${midY}, ${to.x} ${midY}, ${to.x} ${to.y}`,
        ...rendering,
        className: rendering.className,
        stubKind: 'none',
      }
    }
    if (sourceVisible) {
      // Outgoing stub — dangles downward from the visible source into
      // empty space toward the off-canvas target.
      const from = getRenderedWireEndpoint(source, 'out', outSlot)
      const to = { x: from.x, y: from.y + STUB_WIRE_LENGTH }
      return {
        id: `${idPrefix}${source.id}-${target.id}-stubOut`,
        sourceId: source.id,
        targetId: target.id,
        from,
        to,
        path: `M ${from.x} ${from.y} L ${to.x} ${to.y}`,
        strokeWidth: rendering.strokeWidth,
        opacity: rendering.opacity,
        className: `${rendering.className} checklistBoardWireStub checklistBoardWireStub--outgoing`,
        ...(rendering.stroke ? { stroke: rendering.stroke } : {}),
        stubKind: 'outgoing',
      }
    }
    // Incoming stub — target visible, source off-canvas. Tail drops from
    // above the target's top edge down into it.
    const to = getRenderedWireEndpoint(target, 'in', inSlot)
    const from = { x: to.x, y: Math.max(0, to.y - STUB_WIRE_LENGTH) }
    return {
      id: `${idPrefix}${source.id}-${target.id}-stubIn`,
      sourceId: source.id,
      targetId: target.id,
      from,
      to,
      path: `M ${from.x} ${from.y} L ${to.x} ${to.y}`,
      strokeWidth: rendering.strokeWidth,
      opacity: rendering.opacity,
      className: `${rendering.className} checklistBoardWireStub checklistBoardWireStub--incoming`,
      ...(rendering.stroke ? { stroke: rendering.stroke } : {}),
      stubKind: 'incoming',
    }
  }

  // Pass 1 — count incoming wires per target and outgoing wires per source.
  const targetsBySourceId = new Map<string, BoardChecklistItem[]>()
  const sourcesByTargetId = new Map<string, BoardChecklistItem[]>()

  for (const target of boardScopedNodes) {
    const incomingSourceIds = [
      ...target.prerequisites,
      ...(isGateNode(target) ? (target.gateInputIds ?? []) : []),
    ]
    for (const sourceId of incomingSourceIds) {
      const source = nodesById.get(sourceId)
      if (!source) continue

      let outList = targetsBySourceId.get(source.id)
      if (!outList) {
        outList = []
        targetsBySourceId.set(source.id, outList)
      }
      if (!outList.some((t) => t.id === target.id)) {
        outList.push(target)
      }

      let inList = sourcesByTargetId.get(target.id)
      if (!inList) {
        inList = []
        sourcesByTargetId.set(target.id, inList)
      }
      if (!inList.some((s) => s.id === source.id)) {
        inList.push(source)
      }
    }
  }

  const compareRenderedNodes = (a: BoardChecklistItem, b: BoardChecklistItem) => {
    const ra = getRenderedNode(a)
    const rb = getRenderedNode(b)
    const ax = optimisticPositions[ra.id]?.x ?? ra.position?.x ?? 0
    const bx = optimisticPositions[rb.id]?.x ?? rb.position?.x ?? 0
    if (ax !== bx) return ax - bx
    const ay = optimisticPositions[ra.id]?.y ?? ra.position?.y ?? 0
    const by = optimisticPositions[rb.id]?.y ?? rb.position?.y ?? 0
    if (ay !== by) return ay - by
    return ra.id.localeCompare(rb.id)
  }

  targetsBySourceId.forEach((list) => list.sort(compareRenderedNodes))
  sourcesByTargetId.forEach((list) => list.sort(compareRenderedNodes))

  // Pass 2 — build entries with stable indices
  const connectorPaths: ConnectorPath[] = boardScopedNodes.flatMap((target) => {
    const entries: ConnectorPath[] = []
    const inList = sourcesByTargetId.get(target.id) ?? []
    const inCount = inList.length

    for (const sourceId of target.prerequisites) {
      const source = nodesById.get(sourceId)
      if (!source) continue

      const outList = targetsBySourceId.get(source.id) ?? []
      const outCount = outList.length
      const outIndex = outList.findIndex((t) => t.id === target.id)
      const inIndex = inList.findIndex((s) => s.id === source.id)

      const outSlot = outCount > 1 && outIndex >= 0 ? { index: outIndex, count: outCount } : undefined
      const inSlot = inCount > 1 && inIndex >= 0 ? { index: inIndex, count: inCount } : undefined

      const entry = buildConnectorEntry('', source, target, outSlot, inSlot)
      if (entry) entries.push(entry)
    }

    if (isGateNode(target)) {
      for (const sourceId of target.gateInputIds ?? []) {
        const source = nodesById.get(sourceId)
        if (!source) continue

        const outList = targetsBySourceId.get(source.id) ?? []
        const outCount = outList.length
        const outIndex = outList.findIndex((t) => t.id === target.id)
        const inIndex = inList.findIndex((s) => s.id === source.id)

        const outSlot = outCount > 1 && outIndex >= 0 ? { index: outIndex, count: outCount } : undefined
        const inSlot = inCount > 1 && inIndex >= 0 ? { index: inIndex, count: inCount } : undefined

        const entry = buildConnectorEntry('gateIn-', source, target, outSlot, inSlot)
        if (entry) entries.push(entry)
      }
    }
    return entries
  })

  // Trail points come from pointer events on a canvas that overlays the
  // viewport (boardRef's visible area). To intersect cleanly, convert each
  // connector endpoint from WORLD coordinates into that same viewport-visual
  // coordinate space: apply the board's zoom (`boardScale`) and then subtract
  // the scroll offset. Skipping the `* boardScale` step is what previously
  // made the knife slice in the wrong place at any zoom other than 1.
  const sliceableWires: SliceableWire[] = connectorPaths.map((connector) => ({
      id: connector.id,
      sourceId: connector.sourceId,
      targetId: connector.targetId,
      from: toKnifePoint(connector.from, boardScale, viewportSize.scrollLeft, viewportSize.scrollTop),
      to: toKnifePoint(connector.to, boardScale, viewportSize.scrollLeft, viewportSize.scrollTop),
      points: sampleCubicConnectorPath(connector.path, boardScale, viewportSize.scrollLeft, viewportSize.scrollTop),
    }))

  const sliceDependencyWires = async (wireIds: string[]) => {
    if (!canEditCanvas || wireIds.length === 0) return
    const wireIdSet = new Set(wireIds)
    // Split the cut connectors into two groups: gate-INPUT wires (id prefix
    // `gateIn-`, removed from the gate's `gateInputIds`) and regular
    // prerequisite wires (removed from the target's `prerequisites`). Without
    // this split the knife silently no-ops on gate inputs because it would
    // try to remove them from the wrong field.
    const cutConnectors = connectorPaths.filter((connector) => wireIdSet.has(connector.id))
    const gateInputCuts = cutConnectors.filter((connector) => connector.id.startsWith('gateIn-'))
    const prerequisiteCuts = cutConnectors.filter((connector) => !connector.id.startsWith('gateIn-'))

    const removedPrereqsByTarget = new Map<string, Set<string>>()
    const removedGateInputsByTarget = new Map<string, Set<string>>()
    for (const connector of prerequisiteCuts) {
      const set = removedPrereqsByTarget.get(connector.targetId) ?? new Set()
      set.add(connector.sourceId)
      removedPrereqsByTarget.set(connector.targetId, set)
    }
    for (const connector of gateInputCuts) {
      const set = removedGateInputsByTarget.get(connector.targetId) ?? new Set()
      set.add(connector.sourceId)
      removedGateInputsByTarget.set(connector.targetId, set)
    }

    // Stub wires (cross-parent connections whose other endpoint is
    // filtered out of the current breadcrumb view) also need their record
    // updated on cut. The prereq / gateInputIds arrays live on the TARGET
    // node — which may itself be off-canvas when the user is slicing a
    // stub that points INTO an invisible target. Dropping the visibility
    // gate here lets the cut land on the invisible node's record too, so
    // the connection is truly gone once the animation finishes.
    const nextNodes = nodes.map((node) => {
      const droppedPrereqs = removedPrereqsByTarget.get(node.id)
      const droppedInputs = removedGateInputsByTarget.get(node.id)
      if (!droppedPrereqs && !droppedInputs) return node
      return {
        ...node,
        prerequisites: droppedPrereqs
          ? node.prerequisites.filter((id) => !droppedPrereqs.has(id))
          : node.prerequisites,
        gateInputIds: droppedInputs
          ? (node.gateInputIds ?? []).filter((id) => !droppedInputs.has(id))
          : node.gateInputIds,
      }
    })

    const offCanvasCutTitles: string[] = []
    for (const connector of prerequisiteCuts) {
      const source = nodesById.get(connector.sourceId)
      const target = nodesById.get(connector.targetId)
      if (source && target) animateBreakingWire(source, target)
      const sourceVisible = visibleNodeIds.has(connector.sourceId)
      const targetVisible = visibleNodeIds.has(connector.targetId)
      if (!sourceVisible && target) offCanvasCutTitles.push(source?.title ?? 'an off-canvas item')
      else if (!targetVisible && source) offCanvasCutTitles.push(target?.title ?? 'an off-canvas item')
    }
    for (const connector of gateInputCuts) {
      const source = nodesById.get(connector.sourceId)
      const target = nodesById.get(connector.targetId)
      if (source && target) animateBreakingWire(source, target)
      const sourceVisible = visibleNodeIds.has(connector.sourceId)
      const targetVisible = visibleNodeIds.has(connector.targetId)
      if (!sourceVisible && target) offCanvasCutTitles.push(source?.title ?? 'an off-canvas item')
      else if (!targetVisible && source) offCanvasCutTitles.push(target?.title ?? 'an off-canvas item')
    }
    commitLocalNodes(() => nextNodes)
    if (offCanvasCutTitles.length > 0) {
      const [firstTitle, ...rest] = offCanvasCutTitles
      const suffix = rest.length > 0 ? ` and ${rest.length} other${rest.length === 1 ? '' : 's'}` : ''
      showFlash(`Sliced ${wireIds.length} dependency wire${wireIds.length === 1 ? '' : 's'} — disconnected from "${firstTitle}"${suffix} (not visible in current view).`)
    } else {
      showFlash(`Sliced ${wireIds.length} dependency wire${wireIds.length === 1 ? '' : 's'}.`)
    }
  }

  const wireDraftPath = wireDraft ? (() => {
    const source = nodesById.get(wireDraft.sourceId)
    if (!source) return null
    const from = getRenderedAnchorPoint(source, wireDraft.sourceAnchor)
    const to = { x: wireDraft.cursor.x + boardMetrics.offsetX, y: wireDraft.cursor.y + boardMetrics.offsetY }
    const midY = (from.y + to.y) / 2
    return `M ${from.x} ${from.y} C ${from.x} ${midY}, ${to.x} ${midY}, ${to.x} ${to.y}`
  })() : null

  return {
    visibleNodeIds,
    STUB_WIRE_LENGTH,
    connectorPaths,
    sliceableWires,
    sliceDependencyWires,
    wireDraftPath,
  }
}
